import { ArrowDownAZ, ArrowUpAZ } from "lucide-react";
import { sortItemsByText, type SortDir } from "../../lib/sortItems.js";
import { Button } from "./Button.js";

/**
 * Whether `items` is already ordered as `sortItemsByText(items, dir)` would
 * produce. Used to derive the toggle's current-direction indicator directly
 * from data instead of component state, so it stays correct even when the
 * button is unmounted/remounted (e.g. ChecklistPage swaps subgroup markup
 * while a sort is persisting) or when items are reordered by drag-and-drop.
 */
function isSortedBy<T extends { text: string }>(items: T[], dir: SortDir): boolean {
  if (items.length < 2) return false;
  const sorted = sortItemsByText(items, dir);
  return items.every((item, i) => item.text === sorted[i].text);
}

/** Label and the direction a click applies, derived only from item order. */
export function sortButtonState<T extends { text: string }>(
  items: T[]
): { label: string; nextDir: SortDir } {
  if (isSortedBy(items, "asc")) {
    return { label: "Sorted A to Z. Click to sort Z to A.", nextDir: "desc" };
  }
  if (isSortedBy(items, "desc")) {
    return { label: "Sorted Z to A. Click to sort A to Z.", nextDir: "asc" };
  }
  return { label: "Sort A to Z", nextDir: "asc" };
}

// Single toggling button. When the items are already sorted, the label names
// that direction and a click applies the opposite one. Otherwise the click
// sorts A to Z. Direction comes from the items, so it stays correct when the
// button remounts or items are reordered by drag-and-drop.
export function ItemSortButtons<T extends { text: string }>({
  items,
  disabled = false,
  onSort,
}: {
  items: T[];
  disabled?: boolean;
  onSort: (dir: SortDir) => void;
}) {
  const { label, nextDir } = sortButtonState(items);
  const showingDesc = nextDir === "asc" && label.startsWith("Sorted Z");

  return (
    <Button
      type="button"
      variant="secondary"
      size="icon-sm"
      disabled={disabled}
      aria-label={label}
      title={label}
      onClick={() => onSort(nextDir)}
      icon={
        showingDesc ? (
          <ArrowUpAZ size={16} aria-hidden="true" />
        ) : (
          <ArrowDownAZ size={16} aria-hidden="true" />
        )
      }
    />
  );
}
